import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useAuthStore } from '../store/authStore'
import { api } from '../services/api'

export interface AppNotification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  timestamp: number
  read: boolean
  persistent?: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationPayload {
  id: string
  type: string
  title: string
  message: string
  priority: string
  action_url?: string
  action_label?: string
  metadata?: Record<string, unknown>
  created_at?: string
}

interface CommunicationPayload {
  id: string
  title: string
  content: string
  is_read?: boolean
  is_urgent?: boolean
  created_at?: string
}

interface NotificationContextType {
  notifications: AppNotification[]
  addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void
  removeNotification: (id: string) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  clearAll: () => void
  unreadCount: number
  requestNotificationPermission?: () => Promise<NotificationPermission>
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const token = useAuthStore((state) => state.token)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const socketRef = useRef<Socket | null>(null)

  const getTenantId = () => {
    return localStorage.getItem('partner-admin.notifications.tenantId') || ''
  }

  const mergeNotifications = (current: AppNotification[], incoming: AppNotification[]) => {
    const byId = new Map(current.map((item) => [item.id, item]))
    incoming.forEach((item) => {
      if (!byId.has(item.id)) {
        byId.set(item.id, item)
        return
      }
      const existing = byId.get(item.id)
      if (existing) {
        byId.set(item.id, { ...item, read: existing.read || item.read })
      }
    })
    return Array.from(byId.values()).sort((a, b) => b.timestamp - a.timestamp)
  }

  const mapCommunicationToNotification = (payload: CommunicationPayload): AppNotification => {
    const type: AppNotification['type'] = payload.is_urgent ? 'warning' : 'info'
    return {
      id: payload.id,
      type,
      title: payload.title,
      message: payload.content,
      timestamp: payload.created_at ? new Date(payload.created_at).getTime() : Date.now(),
      read: payload.is_read ?? false,
      persistent: true
    }
  }

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
      return
    }

    const socket = io(`${API_BASE_URL}/notifications`, {
      query: { token },
      transports: ['websocket']
    })
    socketRef.current = socket

    socket.on('connect', () => {
      socket.emit('subscribe_notifications')
    })

    socket.on('notifications_history', (payloads: NotificationPayload[]) => {
      const mapped = payloads.map(mapPayloadToNotification)
      setNotifications((prev) => mergeNotifications(prev, mapped))
    })

    socket.on('new_notification', (payload: NotificationPayload) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === payload.id)) return prev
        return [mapPayloadToNotification(payload), ...prev]
      })
    })

    socket.on('notification_read', (data: { notificationId: string }) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === data.notificationId ? { ...n, read: true } : n))
      )
    })

    socket.on('all_notifications_read', () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
    })

    socket.on('unread_count', (data: { count: number }) => {
      setUnreadCount(data.count || 0)
    })

    socket.on('disconnect', () => {
      socketRef.current = null
    })

    socket.on('connect_error', (error) => {
      console.error('Erro ao conectar WebSocket de notificacoes:', error)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [token])

  useEffect(() => {
    if (!token) return

    const fetchHistory = async () => {
      const tenantId = getTenantId()
      if (!tenantId) return
      try {
        const response = await api.get('/communications', {
          headers: {
            'x-tenant-id': tenantId
          },
          params: {
            limit: 50,
            offset: 0
          }
        })
        const historyPayload = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.communications)
            ? response.data.communications
            : []
        const mapped = historyPayload.map(mapCommunicationToNotification)
        setNotifications((prev) => mergeNotifications(prev, mapped))
      } catch (error: any) {
        console.error('Erro ao carregar historico de notificacoes:', error?.response?.data || error?.message || error)
      }
    }

    fetchHistory()
  }, [token])

  const mapPayloadToNotification = (payload: NotificationPayload): AppNotification => {
    const priority = payload.priority || 'medium'
    let type: AppNotification['type'] = 'info'
    if (priority === 'urgent' || priority === 'high') type = 'warning'
    if (priority === 'low') type = 'success'

    return {
      id: payload.id,
      type,
      title: payload.title,
      message: payload.message,
      timestamp: payload.created_at ? new Date(payload.created_at).getTime() : Date.now(),
      read: false,
      persistent: true,
      action: payload.action_url
        ? {
            label: payload.action_label || 'Ver detalhes',
            onClick: () => window.open(payload.action_url || '#', '_blank')
          }
        : undefined
    }
  }

  const addNotification = (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: AppNotification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      read: false
    }

    setNotifications(prev => [newNotification, ...prev])

    // Auto-remover notificações não persistentes após 5 segundos
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(newNotification.id)
      }, 5000)
    }

    // Mostrar notificação do browser se permitido
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: newNotification.id
      })
    }
  }

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
    const tenantId = getTenantId()
    if (tenantId) {
      api.patch(
        `/communications/${id}/mark-read`,
        {},
        {
          headers: {
            'x-tenant-id': tenantId
          }
        }
      ).catch((error) => {
        console.error('Erro ao marcar notificacao como lida:', error?.response?.data || error?.message || error)
      })
    }
    if (socketRef.current) {
      socketRef.current.emit('mark_notification_read', { notificationId: id })
    }
  }

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    )
    if (socketRef.current) {
      socketRef.current.emit('mark_all_read')
    }
  }

  const clearAll = () => {
    setNotifications([])
  }

  const unreadFromState = notifications.filter(n => !n.read).length
  const effectiveUnreadCount = unreadCount > 0 ? unreadCount : unreadFromState

  // Função para solicitar permissão de notificações (deve ser chamada após ação do usuário)
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission
    }
    return Notification.permission
  }

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      markAsRead,
      markAllAsRead,
      clearAll,
      unreadCount: effectiveUnreadCount,
      requestNotificationPermission
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
